<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import CrmAPI from 'dashboard/api/crm';
import CustomerProfile from '../components/CustomerProfile.vue';
import CampaignsPanel from '../components/CampaignsPanel.vue';

const store = useStore();
const contacts = useMapGetter('contacts/getContacts');
const uiFlags = useMapGetter('contacts/getUIFlags');

// aba ativa do módulo CRM ('clientes' | 'campanhas')
const crmTab = ref('clientes');

// contato selecionado -> abre o Perfil 360 (estilo showForm do Disparador)
const selectedContactId = ref(null);
const searchTerm = ref('');
let searchTimer = null;

const fetchContacts = () => {
  store.dispatch('contacts/get', { page: 1, sortAttr: 'name' });
};

// busca com debounce; vazio volta pra listagem padrao
watch(searchTerm, term => {
  clearTimeout(searchTimer);
  const value = term.trim();
  searchTimer = setTimeout(() => {
    if (value) {
      store.dispatch('contacts/search', { search: value, page: 1 });
    } else {
      fetchContacts();
    }
  }, 300);
});

const openProfile = contact => {
  selectedContactId.value = contact.id;
};

const backToList = () => {
  selectedContactId.value = null;
};

const initials = name =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

// -------- modal de importação de planilha --------
const IMPORT_COLUMNS = [
  'nome',
  'telefone',
  'email',
  'data_pedido',
  'valor',
  'produto',
  'quantidade',
  'valor_unitario',
  'destinatario',
  'ocasiao_tipo',
  'ocasiao_data',
  'relacionamento',
  'opt_in',
  'ref',
];

const showImport = ref(false);
const importFile = ref(null);
const isImporting = ref(false);
const importResult = ref(null);
const isDownloadingTemplate = ref(false);
const { t } = useI18n();

// Vendas ganhas no Kanban viram compras (a partir de agora, sozinhas); este
// botao traz as que ja estavam ganhas antes. Roda em segundo plano.
const isImportingKanban = ref(false);
const importKanbanSales = async () => {
  isImportingKanban.value = true;
  try {
    await CrmAPI.importKanbanSales();
    useAlert(t('CRM_RECOMPRA.KANBAN_IMPORT.STARTED'));
  } catch (error) {
    useAlert(t('CRM_RECOMPRA.KANBAN_IMPORT.ERROR'));
  } finally {
    isImportingKanban.value = false;
  }
};

const downloadTemplate = async () => {
  isDownloadingTemplate.value = true;
  try {
    const { data } = await CrmAPI.downloadImportTemplate();
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo-import-clientes.csv';
    a.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    useAlert('Não foi possível baixar o modelo. Tente novamente.');
  } finally {
    isDownloadingTemplate.value = false;
  }
};

const openImport = () => {
  importFile.value = null;
  importResult.value = null;
  showImport.value = true;
};

const closeImport = () => {
  showImport.value = false;
  // ao fechar após um import com sucesso, recarrega a lista de clientes
  if (importResult.value) {
    fetchContacts();
  }
};

const onImportFileChange = event => {
  const [file] = event.target.files || [];
  importFile.value = file || null;
};

const submitImport = async () => {
  if (!importFile.value) {
    useAlert('Selecione um arquivo CSV para importar.');
    return;
  }
  isImporting.value = true;
  try {
    const { data } = await CrmAPI.importSpreadsheet(importFile.value);
    importResult.value = data;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível importar a planilha. Verifique o arquivo e tente novamente.'
    );
  } finally {
    isImporting.value = false;
  }
};

onMounted(fetchContacts);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <template v-if="selectedContactId">
      <CustomerProfile :contact-id="selectedContactId" @back="backToList" />
    </template>

    <template v-else>
      <header class="sticky top-0 z-10 px-6 bg-n-background">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex flex-col gap-0.5">
            <span
              class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12"
            >
              CRM
            </span>
            <span class="text-[13.5px] text-n-slate-10">
              Perfil de compras, datas e recompra dos seus clientes.
            </span>
          </div>
        </div>
        <div class="flex items-center gap-1 pb-3">
          <button
            type="button"
            class="px-3 h-8 rounded-lg text-[13px] font-medium transition-colors"
            :class="
              crmTab === 'clientes'
                ? 'bg-n-blue-3 text-n-blue-9'
                : 'text-n-slate-11 hover:bg-n-slate-2'
            "
            @click="crmTab = 'clientes'"
          >
            Clientes
          </button>
          <button
            type="button"
            class="px-3 h-8 rounded-lg text-[13px] font-medium transition-colors"
            :class="
              crmTab === 'campanhas'
                ? 'bg-n-blue-3 text-n-blue-9'
                : 'text-n-slate-11 hover:bg-n-slate-2'
            "
            @click="crmTab = 'campanhas'"
          >
            Campanhas
          </button>
        </div>
      </header>

      <div v-if="crmTab === 'campanhas'" class="flex-1 px-6 pb-8">
        <CampaignsPanel />
      </div>

      <div v-else class="flex-1 px-6 pb-8">
        <div class="flex flex-col gap-3 pt-1 max-w-4xl mx-auto w-full">
          <div class="flex items-center gap-2">
            <label class="relative flex items-center flex-1 max-w-[420px]">
              <span
                class="i-lucide-search size-4 text-n-slate-10 absolute left-3.5"
              />
              <input
                v-model="searchTerm"
                placeholder="Buscar cliente por nome, telefone ou email..."
                class="h-10 w-full pl-10 pr-3 rounded-lg border border-n-weak bg-n-solid-1 text-[13px] text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand transition-colors"
              />
            </label>
            <Button
              label="Importar planilha"
              icon="i-lucide-upload"
              variant="outline"
              color="slate"
              class="shrink-0 h-10 ml-auto"
              @click="openImport"
            />
          </div>

          <div
            v-if="uiFlags.isFetching"
            class="flex items-center justify-center gap-2 p-10 text-sm text-n-slate-11"
          >
            <span class="i-lucide-loader-circle size-4 animate-spin" />
            Carregando clientes...
          </div>

          <template v-else>
            <div
              v-if="contacts.length"
              class="rounded-2xl border border-n-container bg-n-solid-1 overflow-hidden"
            >
              <button
                v-for="contact in contacts"
                :key="contact.id"
                type="button"
                class="flex flex-row items-center w-full gap-3 px-5 py-3 text-left border-b border-n-slate-3 last:border-b-0 hover:bg-n-slate-2 transition-colors"
                @click="openProfile(contact)"
              >
                <img
                  v-if="contact.thumbnail"
                  :src="contact.thumbnail"
                  class="size-9 rounded-full object-cover shrink-0"
                />
                <span
                  v-else
                  class="flex items-center justify-center size-9 rounded-full bg-n-blue-3 text-xs font-semibold text-n-blue-9 shrink-0"
                >
                  {{ initials(contact.name) }}
                </span>
                <div class="flex flex-col min-w-0 flex-1">
                  <span
                    class="text-[13.5px] font-semibold text-n-slate-12 truncate"
                  >
                    {{ contact.name || 'Sem nome' }}
                  </span>
                  <span class="text-xs text-n-slate-10 truncate">
                    {{ contact.phone_number || contact.email || 'Sem contato' }}
                  </span>
                </div>
                <span class="i-lucide-chevron-right size-4 text-n-slate-10" />
              </button>
            </div>

            <div
              v-if="!contacts.length"
              class="flex flex-col items-center justify-center gap-3 py-24 text-center rounded-2xl border border-n-container bg-n-solid-1"
            >
              <span class="i-lucide-users-round size-10 text-n-slate-9" />
              <p class="text-base font-semibold text-n-slate-12">
                Nenhum cliente encontrado
              </p>
              <p class="text-[13.5px] text-n-slate-10">
                Ajuste a busca ou cadastre contatos para ver o perfil de
                compras.
              </p>
            </div>
          </template>
        </div>
      </div>
    </template>

    <!-- Modal de importação de planilha -->
    <div
      v-if="showImport"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      @click.self="closeImport"
    >
      <div
        class="w-full max-w-2xl max-h-[88vh] flex flex-col rounded-2xl bg-n-solid-1 shadow-xl border border-n-container overflow-hidden"
      >
        <div
          class="flex items-center justify-between gap-3 px-6 py-4 border-b border-n-slate-3"
        >
          <div class="flex items-center gap-3">
            <span
              class="flex items-center justify-center size-10 rounded-full bg-n-blue-3 shrink-0"
            >
              <span class="i-lucide-upload size-5 text-n-blue-9" />
            </span>
            <h2 class="text-[15px] font-semibold text-n-slate-12">
              Importar planilha
            </h2>
          </div>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="sm"
            @click="closeImport"
          />
        </div>

        <div class="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          <!-- Resultado do import -->
          <template v-if="importResult">
            <div
              class="flex items-start gap-2 rounded-lg bg-n-teal-3 px-3 py-2.5 text-[13px] text-n-teal-11"
            >
              <span class="i-lucide-check-check size-4 shrink-0 mt-0.5" />
              <span>
                Importação concluída: {{ importResult.rows_ok }} de
                {{ importResult.rows_total }} linha(s) processada(s).
              </span>
            </div>

            <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div
                class="flex flex-col items-center gap-0.5 rounded-xl border border-n-container bg-n-solid-1 p-3"
              >
                <span
                  class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12"
                >
                  {{ importResult.created_contacts }}
                </span>
                <span class="text-xs text-n-slate-11 text-center"
                  >Contatos criados</span
                >
              </div>
              <div
                class="flex flex-col items-center gap-0.5 rounded-xl border border-n-container bg-n-solid-1 p-3"
              >
                <span
                  class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12"
                >
                  {{ importResult.imported_orders }}
                </span>
                <span class="text-xs text-n-slate-11 text-center"
                  >Pedidos importados</span
                >
              </div>
              <div
                class="flex flex-col items-center gap-0.5 rounded-xl border border-n-container bg-n-solid-1 p-3"
              >
                <span
                  class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12"
                >
                  {{ importResult.created_occasions }}
                </span>
                <span class="text-xs text-n-slate-11 text-center"
                  >Ocasiões criadas</span
                >
              </div>
              <div
                class="flex flex-col items-center gap-0.5 rounded-xl border border-n-container bg-n-solid-1 p-3"
              >
                <span
                  class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12"
                >
                  {{ importResult.opt_in_set }}
                </span>
                <span class="text-xs text-n-slate-11 text-center"
                  >Opt-in marcados</span
                >
              </div>
            </div>

            <div
              v-if="importResult.errors?.length"
              class="flex flex-col gap-1.5"
            >
              <span class="text-sm font-medium text-n-slate-12">
                {{ importResult.errors.length }} linha(s) com problema
              </span>
              <div
                class="max-h-48 overflow-y-auto flex flex-col gap-1 rounded-lg border border-n-container bg-n-solid-1 p-2"
              >
                <div
                  v-for="(err, idx) in importResult.errors"
                  :key="idx"
                  class="flex items-start gap-2 text-xs text-n-slate-11 px-2 py-1.5 border-b border-n-slate-3 last:border-b-0"
                >
                  <span
                    class="i-lucide-triangle-alert size-3.5 shrink-0 mt-0.5 text-n-ruby-11"
                  />
                  <span>
                    <b class="text-n-slate-12">linha {{ err.row }}:</b>
                    {{ err.message }}
                  </span>
                </div>
              </div>
            </div>
          </template>

          <!-- Formulário de upload -->
          <template v-else>
            <label class="flex flex-col gap-1.5">
              <span class="text-[13px] font-medium text-n-slate-11">
                Arquivo CSV
              </span>
              <input
                type="file"
                accept=".csv"
                class="text-[13px] text-n-slate-11 file:mr-3 file:h-9 file:px-3 file:rounded-lg file:border-0 file:bg-n-slate-3 file:text-[13px] file:font-medium file:text-n-slate-12 hover:file:bg-n-slate-4 file:cursor-pointer"
                @change="onImportFileChange"
              />
            </label>

            <div
              class="flex items-center justify-between gap-3 rounded-lg border border-n-container bg-n-slate-2 px-4 py-3"
            >
              <div class="flex flex-col min-w-0">
                <span class="text-[13px] font-semibold text-n-slate-12">
                  Não sabe o formato?
                </span>
                <span class="text-xs text-n-slate-10">
                  Baixe o modelo com as colunas certas e preencha a partir dele.
                </span>
              </div>
              <Button
                label="Baixar modelo (CSV)"
                icon="i-lucide-download"
                variant="outline"
                color="slate"
                size="sm"
                class="shrink-0"
                :is-loading="isDownloadingTemplate"
                :disabled="isDownloadingTemplate"
                @click="downloadTemplate"
              />
            </div>

            <div
              class="flex items-center justify-between gap-3 rounded-lg border border-n-container bg-n-slate-2 px-4 py-3"
            >
              <div class="flex flex-col min-w-0">
                <span class="text-[13px] font-semibold text-n-slate-12">
                  {{ t('CRM_RECOMPRA.KANBAN_IMPORT.TITLE') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ t('CRM_RECOMPRA.KANBAN_IMPORT.HINT') }}
                </span>
              </div>
              <Button
                :label="t('CRM_RECOMPRA.KANBAN_IMPORT.BUTTON')"
                icon="i-lucide-kanban"
                variant="outline"
                color="slate"
                size="sm"
                class="shrink-0"
                :is-loading="isImportingKanban"
                :disabled="isImportingKanban"
                @click="importKanbanSales"
              />
            </div>

            <div
              class="flex flex-col gap-2 rounded-lg border border-n-container bg-n-slate-2 px-4 py-3 text-[13px] text-n-slate-11"
            >
              <p class="font-semibold text-n-slate-12">Colunas aceitas</p>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-for="col in IMPORT_COLUMNS"
                  :key="col"
                  class="inline-flex items-center h-6 px-2 rounded-md bg-n-slate-3 text-xs font-medium text-n-slate-11"
                >
                  {{ col }}
                </span>
              </div>
              <p class="text-xs text-n-slate-10">
                Apenas <b>telefone</b> é obrigatório em cada linha. A coluna
                <b>opt_in</b> (aceita <code>true</code>, <code>1</code> ou
                <code>sim</code>) marca o contato como apto a receber disparos
                de recompra.
              </p>
            </div>
          </template>
        </div>

        <div
          class="flex items-center justify-end gap-2 px-6 py-4 border-t border-n-slate-3"
        >
          <Button
            :label="importResult ? 'Fechar' : 'Cancelar'"
            variant="ghost"
            color="slate"
            @click="closeImport"
          />
          <Button
            v-if="!importResult"
            label="Importar"
            icon="i-lucide-upload"
            :is-loading="isImporting"
            :disabled="isImporting || !importFile"
            @click="submitImport"
          />
        </div>
      </div>
    </div>
  </section>
</template>
